import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { usePageMeta } from '../../lib/usePageMeta';
import { REPORT_PRICE } from '../../lib/pricing';
import { routes } from '../../lib/assessment/routes';
import { buttonVariants } from '../ui/button';
import { cn } from '../../lib/utils';
import { ArrowRight, CheckCircle, ChevronDown } from './icons';

/**
 * The Perspective marketing page, migrated from the legacy application's
 * `src/pages/perspective.tsx` (V2-018C-2).
 *
 * Parity migration. Copy, headings, section order, the eight patterns and their
 * friction/adaptive labelling, both inclusion lists, the FAQ, the price
 * reference, and the structured data are reproduced exactly. The same four
 * categories of change apply as on the previous pages, and only those four:
 * V2 routing, the Experience Shell owning the header and footer,
 * obsolete-technology removal (Clerk), and the accessibility flattening of
 * controls nested inside anchors.
 *
 * Two structural facts are legacy's, not choices made here:
 *
 * - **The CTA precedes the FAQ**, inverting the order used on `/bottleneck` and
 *   `/work-fit`.
 * - **`FULL_REPORT_INCLUDES` is rendered twice** — once as the grid under "What
 *   comes with the full Perspective Report?" and again as the Full Report
 *   card's list in the comparison. Both are reproduced.
 *
 * The legacy footer carried **no disclaimer** on this route, which migration
 * reproduced and recorded as product debt. Launch readiness (Slice E) closes
 * it: `disclaimerForPath` now returns the assessment wording here, the same as
 * the other two assessment pages. Nothing on this page supplies it — the shell
 * footer does.
 */
const PATTERNS = [
  {
    name: 'Future Controller',
    desc: 'Interprets uncertainty as something that must be controlled before acting.',
  },
  {
    name: 'Past Replayer',
    desc: 'Interprets current situations through previous disappointments, regret, or unresolved outcomes.',
  },
  { name: 'Approval Seeker', desc: 'Filters decisions through how others may respond.' },
  { name: 'Comfort Protector', desc: 'Interprets discomfort as danger instead of growth.' },
  { name: 'Outcome Identifier', desc: 'Treats outcomes as identity statements.' },
  { name: 'Negative Forecaster', desc: 'Quickly sees what could go wrong.' },
  { name: 'Self-Debater', desc: 'Overthinks and argues internally before acting.' },
  {
    name: 'Present Operator',
    desc: 'Can prepare for the future, reflect on the past, and act from the present.',
  },
];

const FREE_RESULT_INCLUDES = [
  'Dominant Perspective Pattern',
  'Pattern intensity level',
  'Core interpretive lens in one sentence',
  'Strength of your pattern',
  'Cost when your pattern is overused',
  'One immediate reframe',
  'Score profile across all 8 Perspective Patterns',
];

const FULL_REPORT_INCLUDES = [
  'Dominant Perspective Pattern',
  'Secondary Perspective Pattern',
  'Complete 8-pattern score profile',
  'Key interpretation traps',
  'Self-talk pattern',
  'Time-orientation pattern',
  'Belief friction',
  'Focus/control pattern',
  'One immediate reframe',
  'ABCDE mini reset',
  '7-Day Perspective Reset',
  '30-Day New Mindset Practice',
  'Downloadable PDF',
  '7-Day Perspective Reset Workbook',
  '30-Day New Mindset Practice Workbook',
];

const FAQ_ITEMS = [
  {
    q: 'What does the Perspective Assessment measure?',
    a: 'It measures how you tend to interpret pressure, uncertainty, discomfort, setbacks, success, failure, expectations, focus, control, and yourself. It identifies your dominant interpretation pattern, not a clinical diagnosis or personality label.',
  },
  {
    q: 'Is this a positive mindset score?',
    a: 'No. The Perspective Assessment is not a generic positive-thinking score. It identifies the lens your mind is using, the strength of that lens, the cost when it is overused, and how to shift it.',
  },
  {
    q: 'What do I get with the free result?',
    a: 'Your free result includes your dominant Perspective Pattern, intensity level, core lens, key strength, cost when overused, one immediate reframe, and your score profile across all 8 Perspective Patterns.',
  },
  {
    q: 'What comes with the full Perspective Report?',
    a: 'The full report includes your dominant and secondary patterns, full score profile, interpretation traps, self-talk pattern, time-orientation pattern, belief friction, focus/control pattern, ABCDE reset, 7-Day Perspective Reset, 30-Day New Mindset Practice, downloadable PDF, and two workbook downloads.',
  },
  {
    q: 'Is this clinical or mental health advice?',
    a: 'No. High Performer OS assessments are for educational and coaching purposes only. They do not diagnose, treat, or replace medical or mental health care.',
  },
  {
    q: 'How is Perspective different from Bottleneck and Work Fit?',
    a: 'Bottleneck shows what internal pattern may be creating drag. Work Fit shows whether your environment fits how you operate best. Perspective shows how your mind is interpreting what is happening.',
  },
];

export function Perspective() {
  usePageMeta({
    title: 'High Performer Perspective Assessment | High Performer OS',
    description:
      'Identify the interpretation lens shaping how you read pressure, uncertainty, discomfort, outcomes, and yourself. Take the free Perspective Assessment from High Performer OS.',
    canonical: 'https://highperformeros.com/perspective',
    ogImage: 'https://highperformeros.com/logo-white-v2.webp',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema-perspective';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('faq-schema-perspective');
      if (el) document.head.removeChild(el);
    };
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      {/* Hero */}
      <section className="flex flex-col items-center justify-center px-4 py-24 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 px-4 py-2 rounded-full mb-8">
          High Performer Perspective Assessment · Free
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
          Sometimes the situation is heavy.{' '}
          <span className="text-primary">Sometimes the lens makes it heavier.</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed">
          Pressure, uncertainty, discomfort, and outcomes are not just situations. They are events
          that get interpreted. The lens your mind uses to read them shapes what you feel, what you
          decide, and how you perform. The Perspective Assessment identifies which lens is most
          active for you.
        </p>
        <Link
          href={routes.assessment('perspective')}
          className={cn(
            buttonVariants({ size: 'lg' }),
            'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(245,184,0,0.25)] gap-2',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          )}
        >
          Take the Perspective Assessment
          <ArrowRight className="w-4 h-4" />
        </Link>
        <p className="text-xs text-muted-foreground mt-4">
          50 questions. Free. No credit card required.
        </p>
      </section>

      {/* AISEO Answer Block */}
      <section className="py-16 px-4 border-y border-border bg-card">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What is the Perspective Assessment?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The Perspective Assessment is a 50-question diagnostic that identifies the
              interpretation lens your mind uses when processing pressure, uncertainty, discomfort,
              setbacks, success, failure, expectations, and yourself. It surfaces your dominant
              Perspective Pattern and its intensity level. The result is free.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              Who is it for?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              It is for high performers who notice their mind repeatedly interpreting situations in
              ways that create friction, hesitation, or inconsistency. If your thinking about a
              situation often feels more draining than the situation itself, a perspective pattern
              is likely active.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What does it measure?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              It measures 8 perspective patterns: Future Controller, Past Replayer, Approval Seeker,
              Comfort Protector, Outcome Identifier, Negative Forecaster, Self-Debater, and Present
              Operator. Your highest-scoring pattern determines your dominant Perspective Pattern.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What do you get for free?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your free result includes your dominant Perspective Pattern, pattern intensity level,
              core interpretive lens, key strength, cost when overused, one immediate reframe, and
              your score profile across all 8 Perspective Patterns. No payment required.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What is included in the full report?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The full report includes your dominant and secondary patterns, complete 8-pattern
              score profile, interpretation traps, self-talk pattern, time-orientation pattern,
              belief friction, focus/control pattern, ABCDE reset, 7-Day Perspective Reset, 30-Day
              New Mindset Practice, downloadable PDF, and two workbook downloads. It is a one-time
              purchase unlocked after your free result.
            </p>
          </div>
        </div>
      </section>

      {/* What it helps you see */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            What the Perspective Assessment helps you see
          </p>
          <h2 className="text-2xl md:text-3xl font-bold">
            The situation creates some of the weight. The lens creates the rest.
          </h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed text-left">
            <p>
              The same situation can be processed through many different lenses. One person sees a
              challenge as a threat. Another sees it as data. A third replays a similar failure from
              three years ago. The difference in outcome is not only ability or effort. It is how
              the situation is being read.
            </p>
            <p>
              Perspective patterns are not flaws. They are habits of interpretation that developed
              for real reasons and often carry genuine strengths. The cost appears when they run
              automatically in situations where a different lens would serve you better.
            </p>
            <p>
              The Perspective Assessment identifies which pattern is most active for you, how
              intense it currently is, and what to do about it.
            </p>
          </div>
        </div>
      </section>

      {/* What does the Perspective Assessment measure? */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
              The 8 Perspective Patterns
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              What does the Perspective Assessment measure?
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
              The Perspective Assessment measures the lens your mind tends to use when interpreting
              pressure, uncertainty, discomfort, setbacks, success, failure, expectations, focus,
              control, and yourself. It does not tell you whether you have a good or bad mindset. It
              identifies the interpretation pattern most likely shaping your self-talk, decisions,
              and next action.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PATTERNS.map((p) => (
              <div
                key={p.name}
                className={`p-5 rounded-xl border bg-card space-y-2 ${p.name === 'Present Operator' ? 'border-primary/30' : 'border-border'}`}
              >
                <p
                  className={`text-xs font-bold uppercase tracking-widest ${p.name === 'Present Operator' ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  {p.name === 'Present Operator' ? 'Adaptive Capacity' : 'Friction Pattern'}
                </p>
                <h3 className="font-semibold text-sm">{p.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What comes with the full Perspective Report? */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
              Full Perspective Report
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              What comes with the full Perspective Report?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              The free result shows your headline pattern. The full report explains how that pattern
              operates. The workbook helps you move forward by practicing a new response: catching
              the lens, challenging the belief, replacing the self-talk, and acting from the
              present.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl mx-auto">
            {FULL_REPORT_INCLUDES.map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 p-4 rounded-xl border border-border bg-background"
              >
                <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-snug">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            Unlocked after taking the free assessment.
          </p>
        </div>
      </section>

      {/* Free result or full report? */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
              Compare
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">Free result or full report?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Start with the free result. Unlock the full report when you want the deeper pattern
              breakdown and a practical way to create a new mindset pattern.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Result */}
            <div className="rounded-2xl border border-border bg-background p-7 space-y-5">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Free
                </span>
                <h3 className="text-xl font-bold mt-1">Free Result</h3>
              </div>
              <ul className="space-y-3">
                {FREE_RESULT_INCLUDES.map((item) => (
                  <li key={item} className="flex gap-3 items-start text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                href={routes.assessment('perspective')}
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'w-full mt-2',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                )}
              >
                Take the free assessment
              </Link>
            </div>

            {/* Full Report */}
            <div className="rounded-2xl border-2 border-primary/40 bg-primary/5 p-7 space-y-5 relative">
              <div className="absolute -top-3 left-6">
                <span className="text-xs font-bold uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1 rounded-full">
                  {`Full Report · $${REPORT_PRICE}`}
                </span>
              </div>
              <div className="pt-2">
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  One-time payment
                </span>
                <h3 className="text-xl font-bold mt-1">Full Report and Workbooks</h3>
              </div>
              <ul className="space-y-3">
                {FULL_REPORT_INCLUDES.map((item) => (
                  <li key={item} className="flex gap-3 items-start text-sm">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">Unlock after your result.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-card border-y border-border py-24 px-4 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">Name the pattern. Change the default.</h2>
          <p className="text-muted-foreground leading-relaxed">
            You cannot shift an interpretive pattern you cannot see. The Perspective Assessment
            gives you the clarity to work on the right thing.
          </p>
          <Link
            href={routes.assessment('perspective')}
            className={cn(
              buttonVariants({ size: 'lg' }),
              'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 gap-2',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            )}
          >
            Take the Perspective Assessment
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-muted-foreground">Free. No credit card required.</p>
          <p className="text-sm text-muted-foreground">
            Looking for Bottleneck instead?{' '}
            <Link
              href={routes.bottleneck}
              className="underline hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              Explore the Bottleneck Assessment.
            </Link>
          </p>
          <p className="text-sm text-muted-foreground">
            Looking for Work Fit instead?{' '}
            <Link
              href={routes.workFit}
              className="underline hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              Explore the Work Fit Assessment.
            </Link>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            Frequently asked questions
          </h2>
          <div className="space-y-3" data-testid="faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className="border border-border rounded-xl overflow-hidden">
                <button
                  type="button"
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-panel-${i}`}
                  data-testid={`faq-trigger-${i}`}
                  className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold hover:bg-card transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {item.q}
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 ml-4 text-muted-foreground transition-transform duration-150 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div
                    id={`faq-panel-${i}`}
                    className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3"
                  >
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export { FAQ_ITEMS as PERSPECTIVE_FAQ_ITEMS, PATTERNS, FREE_RESULT_INCLUDES, FULL_REPORT_INCLUDES };
